/**
 * ChatPanel — Drop-in Universal Chat UI
 * ─────────────────────────────────────
 * Renders sidebar + chat area. Special-case for `context.type === 'support'`:
 *   - Non-admin viewers see it as "GIGGS Support" and it's pinned to top
 *   - Admins see the user's real name with a small "Support" badge
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../../context/ChatContext';

const formatTime = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' });
  return date.toLocaleDateString();
};

const initials = (name = '') =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

const SupportBadge = ({ children }) => (
  <span className="ml-1.5 inline-block rounded-[10px] bg-giggs-green/10 px-2 py-0.5 align-middle font-dm text-[10px] font-semibold tracking-[0.3px] text-giggs-green">
    {children}
  </span>
);

const TypingDots = () => (
  <div className="inline-flex items-center gap-[5px] px-0.5 py-1" aria-label="Typing">
    <span className="h-2 w-2 animate-bounce rounded-full bg-giggs-muted [animation-delay:-0.32s]" />
    <span className="h-2 w-2 animate-bounce rounded-full bg-giggs-muted [animation-delay:-0.16s]" />
    <span className="h-2 w-2 animate-bounce rounded-full bg-giggs-muted" />
  </div>
);

const TypingLabel = () => <span className="font-dm text-[11px] italic text-giggs-green">typing…</span>;

const getConvoDisplay = (conversation, currentUserId, currentUserRole) => {
  const isSupport = conversation.context?.type === 'support';
  const other = (conversation.participants || []).find(
    (participant) => String(participant._id) !== String(currentUserId)
  );

  if (isSupport && currentUserRole !== 'admin') {
    return {
      name: 'GIGGS Support',
      avatar: 'GS',
      brandedAvatar: true,
      isSupport: true,
      other,
    };
  }

  if (isSupport && currentUserRole === 'admin') {
    return {
      name: other?.name || 'User',
      avatar: initials(other?.name),
      brandedAvatar: false,
      isSupport: true,
      showBadge: 'Support',
      other,
    };
  }

  const name = conversation.isGroup ? conversation.name : other?.name || 'Unknown';
  return {
    name,
    avatar: initials(name),
    brandedAvatar: false,
    isSupport: false,
    other,
  };
};

const ChatPanel = ({
  initialConversationId = null,
  awaitInitialResolution = false,
  contextFilter = null,
  onOpenConversation,
  emptyText = 'Select a conversation to start messaging',
}) => {
  const {
    conversations,
    activeConversationId,
    activeConversation,
    messages,
    typingUsers,
    onlineUserIds,
    connected,
    currentUserId,
    currentUserRole,
    setActiveConversation,
    sendMessage,
    startTyping,
    stopTyping,
  } = useChat();

  const [newMessage, setNewMessage] = useState('');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);
  const initRef = useRef(false);

  const visibleConvos = useMemo(() => {
    let list = conversations;
    if (contextFilter?.type) list = list.filter((conversation) => conversation.context?.type === contextFilter.type);
    if (search.trim()) {
      const query = search.toLowerCase();
      list = list.filter((conversation) => {
        const display = getConvoDisplay(conversation, currentUserId, currentUserRole);
        const preview = conversation.lastMessage?.content || '';
        return display.name.toLowerCase().includes(query) || preview.toLowerCase().includes(query);
      });
    }
    return [...list].sort((a, b) => {
      if (currentUserRole !== 'admin') {
        const aSupport = a.context?.type === 'support';
        const bSupport = b.context?.type === 'support';
        if (aSupport && !bSupport) return -1;
        if (!aSupport && bSupport) return 1;
      }
      return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
    });
  }, [conversations, contextFilter, search, currentUserId, currentUserRole]);

  useEffect(() => {
    if (initRef.current) return;
    if (!conversations.length) return;
    if (awaitInitialResolution && !initialConversationId) return;
    if (initialConversationId) {
      const exists = conversations.find((conversation) => conversation._id === initialConversationId);
      if (exists) {
        setActiveConversation(initialConversationId);
        initRef.current = true;
        return;
      }
    }
    if (visibleConvos[0]) {
      setActiveConversation(visibleConvos[0]._id);
      initRef.current = true;
    }
  }, [conversations, visibleConvos, initialConversationId, awaitInitialResolution, setActiveConversation]);

  const activeMessages = activeConversationId ? messages[activeConversationId] || [] : [];

  const isOtherTyping = useMemo(() => {
    if (!activeConversationId) return false;
    const set = typingUsers[activeConversationId];
    if (!set) return false;
    return Array.from(set).some((id) => String(id) !== String(currentUserId));
  }, [typingUsers, activeConversationId, currentUserId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeMessages.length, activeConversationId, isOtherTyping]);

  const activeDisplay = useMemo(() => {
    if (!activeConversation) return null;
    return getConvoDisplay(activeConversation, currentUserId, currentUserRole);
  }, [activeConversation, currentUserId, currentUserRole]);

  const handleSend = async (event) => {
    event.preventDefault();
    const text = newMessage.trim();
    if (!text || !activeConversationId || sending) return;
    setSending(true);
    setNewMessage('');
    stopTyping(activeConversationId);
    const response = await sendMessage(activeConversationId, text);
    if (!response.ok) {
      console.error('[chat] send failed:', response.error);
      setNewMessage(text);
    }
    setSending(false);
  };

  const handleInputChange = (event) => {
    setNewMessage(event.target.value);
    if (activeConversationId) startTyping(activeConversationId);
  };

  const handleSelectConvo = (id) => {
    setActiveConversation(id);
    onOpenConversation?.(id);
  };

  const avatarClass = (branded, size = 'list') => {
    const dimensions = size === 'header' ? 'h-9 w-9 min-w-9 text-sm' : 'h-10 w-10 min-w-10 text-[13px]';
    return `relative flex shrink-0 items-center justify-center rounded-full font-nunito font-extrabold ${dimensions} ${
      branded
        ? 'bg-gradient-to-br from-giggs-green to-giggs-mint text-white'
        : 'bg-gradient-to-br from-[#D3F9E0] to-[#AAF0C4] text-[#007038]'
    }`;
  };

  const OnlineDot = () => <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-500" />;

  const renderConvoListItem = (conversation) => {
    const display = getConvoDisplay(conversation, currentUserId, currentUserRole);
    const online = display.other && onlineUserIds.has(String(display.other._id));
    const preview = conversation.lastMessage?.content || 'No messages yet';
    const time = conversation.lastMessage?.sentAt ? formatTime(conversation.lastMessage.sentAt) : formatTime(conversation.updatedAt);
    const sidebarTyping = (() => {
      const set = typingUsers[conversation._id];
      if (!set || set.size === 0) return false;
      return Array.from(set).some((id) => String(id) !== String(currentUserId));
    })();
    const active = activeConversationId === conversation._id;

    return (
      <button
        type="button"
        key={conversation._id}
        className={`relative flex w-full cursor-pointer items-start gap-3 border-0 border-b border-[#F8FAF9] bg-white px-3 py-3 text-left transition-colors sm:px-5 sm:py-4 ${
          active ? 'border-l-[3px] border-l-giggs-green bg-[rgba(0,166,81,0.05)]' : 'hover:bg-slate-50'
        }`}
        onClick={() => handleSelectConvo(conversation._id)}
      >
        <span className={avatarClass(display.brandedAvatar)}>
          {display.avatar}
          {online && <OnlineDot />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="mb-0.5 flex items-center justify-between gap-2">
            <span className="min-w-0 truncate font-nunito text-sm font-bold text-giggs-ink">
              {display.name}
              {display.showBadge && <SupportBadge>{display.showBadge}</SupportBadge>}
            </span>
            <span className="shrink-0 font-dm text-[11px] text-giggs-faint">{time}</span>
          </span>
          {conversation.context?.type && conversation.context.type !== 'support' && (
            <span className="mb-[3px] block truncate font-dm text-[11px] font-medium text-giggs-green">
              {conversation.context.type} · {String(conversation.context.refId || '').slice(-6)}
            </span>
          )}
          <span className="block truncate font-dm text-[13px] text-giggs-muted">
            {sidebarTyping ? <TypingLabel /> : preview}
          </span>
        </span>
        {conversation.unreadCount > 0 && (
          <span className="absolute right-2 top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-giggs-green px-1 text-[11px] font-bold text-white sm:right-4 sm:top-4">
            {conversation.unreadCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 font-dm">
      <aside className="flex h-full w-[42%] min-w-[145px] max-w-[260px] shrink-0 flex-col overflow-hidden border-r border-slate-200 bg-white sm:w-[260px] sm:min-w-[260px] md:w-[340px] md:min-w-[340px] md:max-w-[340px]">
        <div className="flex items-center gap-2.5 border-b border-slate-100 px-3 py-3 sm:px-5 sm:py-4">
          <svg width="16" height="16" fill="none" stroke="#8A9B91" strokeWidth="2" viewBox="0 0 24 24" className="shrink-0">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            className="min-w-0 flex-1 border-0 bg-transparent p-0 font-dm text-sm text-giggs-ink outline-none placeholder:text-giggs-faint"
            placeholder="Search conversations..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {visibleConvos.length === 0 ? (
            <div className="p-4 text-[13px] text-giggs-muted sm:p-6">No conversations yet.</div>
          ) : visibleConvos.map(renderConvoListItem)}
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col overflow-hidden bg-slate-50">
        {activeConversation && activeDisplay ? (
          <>
            <header className="flex items-center justify-between border-b border-slate-200 bg-white px-3 py-3 sm:px-6 sm:py-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className={avatarClass(activeDisplay.brandedAvatar, 'header')}>
                  {activeDisplay.avatar}
                  {activeDisplay.other && onlineUserIds.has(String(activeDisplay.other._id)) && <OnlineDot />}
                </span>
                <div className="min-w-0">
                  <span className="block truncate font-nunito text-[15px] font-bold text-giggs-ink">
                    {activeDisplay.name}
                    {activeDisplay.showBadge && <SupportBadge>{activeDisplay.showBadge}</SupportBadge>}
                  </span>
                  <span className="flex items-center gap-1.5 truncate font-dm text-xs text-giggs-muted">
                    {isOtherTyping ? <TypingLabel /> : activeDisplay.other && onlineUserIds.has(String(activeDisplay.other._id)) ? 'Online' : connected ? 'Offline' : 'Connecting…'}
                  </span>
                </div>
              </div>
            </header>

            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3 sm:p-6">
              {activeMessages.length === 0 ? (
                <div className="m-auto max-w-sm text-center text-[13px] leading-6 text-giggs-muted">
                  {activeDisplay.isSupport && currentUserRole !== 'admin'
                    ? 'Have a question? Send us a message — our team will get back to you shortly.'
                    : 'Say hello 👋'}
                </div>
              ) : (
                activeMessages.map((message) => {
                  const isMe = String(message.sender?._id) === String(currentUserId);
                  return (
                    <div key={message._id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`relative max-w-[85%] rounded-2xl px-3 py-2.5 sm:max-w-[70%] sm:px-4 sm:py-3 ${
                        isMe
                          ? 'rounded-br bg-giggs-green text-white'
                          : 'rounded-bl border border-slate-200 bg-white text-giggs-ink'
                      }`}>
                        <p className="m-0 whitespace-pre-wrap break-words font-dm text-sm leading-[1.6]">{message.content}</p>
                        <span className={`mt-1.5 block font-dm text-[10px] opacity-70 ${isMe ? 'text-right text-white/70' : 'text-giggs-faint'}`}>
                          {formatTime(message.createdAt)}
                          {isMe && activeDisplay.other && message.readBy?.some((id) => String(id) === String(activeDisplay.other._id)) && ' · seen'}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}

              {isOtherTyping && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-bl border border-slate-200 bg-white px-4 py-3 text-giggs-ink"><TypingDots /></div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <form className="flex items-center gap-2 border-t border-slate-200 bg-white p-3 sm:gap-3 sm:px-6 sm:py-4" onSubmit={handleSend}>
              <input
                className="box-border min-w-0 flex-1 rounded-xl border border-transparent bg-slate-100 px-3 py-3 font-dm text-sm text-giggs-ink outline-none transition-colors placeholder:text-giggs-faint focus:border-giggs-green focus:bg-white sm:px-[18px]"
                placeholder="Type a message..."
                value={newMessage}
                onChange={handleInputChange}
                disabled={sending}
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sending}
                aria-label={sending ? 'Sending...' : 'Send'}
                className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border-0 bg-giggs-green text-white transition-all hover:bg-giggs-green-hover hover:shadow-[0_4px_12px_rgba(0,166,81,0.25)] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:shadow-none"
              >
                {sending ? (
                  <span className="inline-block h-[18px] w-[18px] animate-spin rounded-full border-2 border-white/35 border-t-white" />
                ) : (
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                )}
              </button>
            </form>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-center text-[13px] text-giggs-muted">
            <p>{emptyText}</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default ChatPanel;
